import apiClient from './api';
import { API_ENDPOINTS } from '@/config/api.config';
import { mockCertificationData } from './mock/certifications.mock';

/** One selected course, learning path or document, judged against the description. */
export interface ContentSelectionItemVerdict {
  type: 'course' | 'learning_path' | 'document';
  id: string;
  name: string;
  relevant: boolean;
  reason: string;
  note?: string;
}

export interface ContentSelectionValidationResult {
  /** ok = submit silently · warn = show flagged items, allow continue · block = nothing matches */
  outcome: 'ok' | 'warn' | 'block';
  set_supports_description: boolean;
  summary: string;
  items: ContentSelectionItemVerdict[];
  irrelevant_count: number;
  /** True when the check could not run. Outcome is then 'ok' — never block on a failed check. */
  check_skipped: boolean;
}

// Interfaces for type safety
interface Filters {
  status?: string;
  course?: string;
  search?: string;
  page?: number;
  pageSize?: number;
  certificationId?: string;
  userId?: string;
}

interface CertificationData {
  [key: string]: any;
}

interface TemplateData {
  [key: string]: any;
}

interface ProgressData {
  [key: string]: any;
}

// User Certification Summary interface matching API response
export interface UserCertificationSummaryDto {
  certification_id: string;
  title: string;
  description?: string;
  banner_image_url?: string;
  thumbnail_image_url?: string;
  time_limit_minutes?: number;
  rating?: number;
  review_count?: number;
  assignment_id?: string;
  createdAt?: Date;
  updatedAt?: Date;
  status: 'NOT_STARTED' | 'IN_PROGRESS' | 'COMPLETED' | 'EXPIRED';
  is_mandatory: boolean;
  is_published: boolean;
  deadline_date?: Date;
  last_accessed_at?: Date;
  total_time_spent_seconds?: number;
  started_at?: Date;
  completed_at?: Date;
  allow_retry?: boolean; // Allow retry flag from exam settings
  show_feedback?: boolean; // Show feedback flag from exam settings
  show_correct_answers?: boolean; // Show correct answers flag from exam settings
  max_attempts?: number; // Max attempts allowed from exam settings
  certificate_issued?: boolean; // True if certificate has been issued to the user
  certificate_number?: string; // Certificate number for claiming
}

// Minimal Certification List interfaces
interface MinimalCertificationListQueryDto {
  page?: number;
  limit?: number;
  is_published?: boolean;
  min_time_limit?: number;
  max_time_limit?: number;
  min_passing_score?: number;
  max_passing_score?: number;
  search?: string;
  sortBy?: 'title' | 'time_limit' | 'passing_score' | 'total_points' | 'question_count' | 'createdAt' | 'updatedAt';
  sortOrder?: 'asc' | 'desc';
}

interface PaginationDto {
  page: number;
  limit: number;
  totalPages: number;
  totalItems: number;
}

interface MinimalCertificationListItemDto {
  _id: string;
  certification_id: string;
  title: string;
  description: string;
  banner_image_url?: string;
  thumbnail_image_url?: string;
  is_published: boolean;
  time_limit_minutes: number;
  passing_score: number;
  total_points: number;
  question_count: number;
  version: number;
  settings: {
    shuffle_questions: boolean;
    shuffle_options: boolean;
    show_feedback: boolean;
    show_correct_answers: boolean;
    allow_retry: boolean;
    max_attempts: number;
  };
  createdAt: Date;
  updatedAt: Date;
}

interface MinimalCertificationListResponseDto {
  certifications: MinimalCertificationListItemDto[];
  pagination: PaginationDto;
  total: number;
  published_count: number;
  unpublished_count: number;
}

// Recent Certification Generations interfaces
interface CertificationFilterDto {
  status?: 'DRAFT' | 'ACTIVE' | 'INACTIVE' | 'ARCHIVED';
  targeted_audience?: string;
  search?: string;
  limit?: number;
  skip?: number;
}

interface GenerationStepStatus {
  status: 'pending' | 'in_progress' | 'processing' | 'completed' | 'failed';
  started_at?: Date;
  completed_at?: Date;
  duration_ms?: number;
  error?: string;
}

interface CertificationGenerationStatus {
  overall_status: 'pending' | 'in_progress' | 'processing' | 'completed' | 'failed';
  validation?: GenerationStepStatus;
  content_extraction?: GenerationStepStatus;
  ai_generation?: GenerationStepStatus;
  exam_creation?: GenerationStepStatus;
  metadata_generation?: GenerationStepStatus;
  image_generation?: GenerationStepStatus;
  question_generation?: GenerationStepStatus;
  question_progress?: {
    total_questions: number;
    generated: number;
  };
  questions_generated?: number;
  questions_failed?: number;
  started_at?: Date;
  completed_at?: Date;
  token_usage?: number;
}

interface RecentCertification {
  _id: string;
  certification_name: string;
  description?: string;
  targeted_audience?: string;
  status: CertificationStatus;
  version: number;
  required_course_ids: string[];
  exam_config: ExamConfig;
  certification_exam_id?: string;
  validity_type: ValidityType;
  created_by: string;
  total_enrolled: number;
  total_certified: number;
  is_active: boolean;
  createdAt: Date;
  updatedAt: Date;
  generation_status?: CertificationGenerationStatus;
}

interface RecentCertificationListResponseDto {
  certifications: RecentCertification[];
  total: number;
  pagination: {
    total?: number;
    limit: number;
    skip: number;
    totalPages: number;
  };
}

// Certification Details interfaces
export interface QuestionOption {
  id: string;
  text: string;
  is_correct: boolean;
  explanation?: string;
}

export type QuestionType = 'SINGLE_CHOICE' | 'MULTIPLE_CHOICE' | 'TRUE_FALSE' | 'TEXT_ANSWER';
export type DifficultyLevel = 'EASY' | 'MEDIUM' | 'HARD';
export type QuestionSource = 'AI_GENERATED' | 'MANUAL';

export interface CertificationExamQuestion {
  id: string;
  question_text: string;
  question_type: QuestionType;
  options?: QuestionOption[];
  correct_answer?: string;
  answer_keywords?: string[];
  case_sensitive?: boolean;
  points: number;
  difficulty_level: DifficultyLevel;
  bloom_level?: string;
  topic?: string;
  course_id?: string;
  explanation?: string;
  hint?: string;
  source: QuestionSource;
  order: number;
  created_at?: string;
  updated_at?: string;
}

export interface ExamConfig {
  total_questions: number;
  question_types: QuestionType[];
  difficulty_distribution: {
    easy_percentage: number;
    medium_percentage: number;
    hard_percentage: number;
  };
  time_limit_minutes?: number;
  passing_score: number;
  shuffle_questions: boolean;
  shuffle_options: boolean;
  show_feedback: boolean;
  show_correct_answers: boolean;
  allow_retry: boolean;
  max_attempts?: number;
}

export interface CertificationExam {
  id: string;
  certification_id: string;
  title: string;
  description?: string;
  banner_image_url?: string;
  thumbnail_image_url?: string;
  questions: CertificationExamQuestion[];
  total_points: number;
  generated_by: 'AI_GENERATED' | 'MANUAL' | 'HYBRID';
  version: number;
  is_published: boolean;
  time_limit_minutes?: number;
  passing_score: number;
  shuffle_questions: boolean;
  shuffle_options: boolean;
  show_feedback: boolean;
  show_correct_answers: boolean;
  allow_retry: boolean;
  max_attempts?: number;
  tier_config?: TierConfig;
  created_at: string;
  updated_at: string;
}

export interface TierConfig {
  enabled: boolean;
  gold_threshold: number;
  silver_threshold: number;
  bronze_threshold: number;
}

export type CertificationStatus = 'DRAFT' | 'ACTIVE' | 'INACTIVE' | 'ARCHIVED';
export type ValidityType = 'LIFETIME' | 'FIXED_DURATION' | 'RENEWAL_REQUIRED';

export interface Certification {
  show_feedback?: boolean;
  show_correct_answers?: boolean;
  allow_retry?: boolean;
  max_attempts?: number | undefined;
  shuffle_options?: boolean;
  shuffle_questions?: boolean;
  _id: string;
  banner_image_url: any;
  is_published: any;
  thumbnail_image_url: string | undefined;
  title: string;
  total_points: number;
  time_limit_minutes: any;
  passing_score: number;
  questions: any;
  id: string;
  certification_name: string;
  slug?: string;
  description?: string;
  tags?: string[];
  targeted_audience?: string;
  status: CertificationStatus;
  version: number;
  required_course_ids: string[];
  exam_config: ExamConfig;
  certification_exam_id?: string;
  validity_type: ValidityType;
  validity_months?: number;
  certificate_template_ids?: { GOLD?: string; SILVER?: string; BRONZE?: string }; // missing tier = default template
  created_by: string;
  updated_by?: string;
  published_at?: string;
  archived_at?: string;
  total_enrolled: number;
  total_certified: number;
  is_active: boolean;
  created_at: string;
  updated_at: string;
  exam?: CertificationExam;
  tier_config?: TierConfig;
  rating?: number;
  review_count?: number;
}

export interface AddQuestionDto {
  id?: string;
  question_text: string;
  question_type: QuestionType;
  options?: QuestionOption[];
  correct_answer?: string;
  answer_keywords?: string[];
  case_sensitive?: boolean;
  points: number;
  difficulty_level: DifficultyLevel;
  bloom_level?: string;
  topic?: string;
  course_id?: string;
  explanation?: string;
  hint?: string;
  source?: QuestionSource;
  order: number;
}

export interface CreateManualCertificationDto {
  title: string;
  description: string;
  targeted_audience?: string;
  tags?: string[];
  time_limit_minutes?: number;
  passing_score?: number;
  shuffle_questions?: boolean;
  shuffle_options?: boolean;
  show_feedback?: boolean;
  show_correct_answers?: boolean;
  allow_retry?: boolean;
  max_attempts?: number;
  validity_type?: 'LIFETIME' | 'FIXED_DURATION';
  validity_months?: number;
}

export interface UpdateExamSettingsDto {
  title?: string;
  description?: string;
  tags?: string[];
  banner_image_url?: string;
  thumbnail_image_url?: string;
  time_limit_minutes?: number;
  passing_score?: number;
  shuffle_questions?: boolean;
  shuffle_options?: boolean;
  show_feedback?: boolean;
  show_correct_answers?: boolean;
  allow_retry?: boolean;
  max_attempts?: number | null; // null = unlimited attempts
  tier_config?: TierConfig;
}

class CertificationService {
  private useMockData: boolean;

  constructor() {
    this.useMockData = false; // Switch to false when real API is ready
  }

  // Certification Management
  async getCertifications(filters: Filters = {}): Promise<any> {
    if (this.useMockData) {
      return new Promise(resolve => {
        setTimeout(() => {
          let certifications = [...mockCertificationData.certifications];

          if (filters.status) {
            certifications = certifications.filter(c => c.status === filters.status);
          }
          if (filters.course) {
            certifications = certifications.filter(c => c.courseId === filters.course);
          }
          if (filters.search) {
            const searchLower = filters.search.toLowerCase();
            certifications = certifications.filter(
              c =>
                c.name.toLowerCase().includes(searchLower) ||
                c.description.toLowerCase().includes(searchLower)
            );
          }

          resolve({
            data: certifications,
            total: certifications.length,
            page: filters.page || 1,
            pageSize: filters.pageSize || 10,
          });
        }, 500);
      });
    }
    // Real API call would go here
    return Promise.resolve({ data: [], total: 0, page: 1, pageSize: 10 });
  }

  async getCertificationById(id: string): Promise<Certification> {
    try {
      const response = await apiClient.get(API_ENDPOINTS.CERTIFICATIONS.GET_BY_ID(id));
      return response.data.data;
    } catch (error) {
      console.error('Error fetching certification by ID:', error);
      throw error;
    }
  }

  async getMinimalCertifications(params: MinimalCertificationListQueryDto = {}): Promise<MinimalCertificationListResponseDto> {
    try {
      const response = await apiClient.get(API_ENDPOINTS.CERTIFICATIONS.MINIMAL_LIST, { params });
      // API wraps the actual data in response.data.data
      return response.data.data;
    } catch (error) {
      console.error('Error fetching minimal certifications:', error);
      throw error;
    }
  }

  async createCertification(certificationData: CertificationData): Promise<any> {
    try {
      const response = await apiClient.post(API_ENDPOINTS.CERTIFICATIONS.CREATE, certificationData);
      return response.data;
    } catch (error) {
      console.error('Error creating certification:', error);
      throw error;
    }
  }

  // Manual (admin-authored) certification: creates the certification and an empty exam, no AI
  async createManualCertification(data: CreateManualCertificationDto): Promise<Certification> {
    const response = await apiClient.post(API_ENDPOINTS.CERTIFICATIONS.CREATE_MANUAL, data);
    return response.data.data;
  }

  async updateCertification(id: string, certificationData: CertificationData): Promise<any> {
    if (this.useMockData) {
      return new Promise((resolve, reject) => {
        setTimeout(() => {
          const index = mockCertificationData.certifications.findIndex(c => c.id === id);
          if (index !== -1) {
            mockCertificationData.certifications[index] = {
              ...mockCertificationData.certifications[index],
              ...certificationData,
              updatedAt: new Date().toISOString(),
            };
            resolve(mockCertificationData.certifications[index]);
          } else {
            reject(new Error('Certification not found'));
          }
        }, 1000);
      });
    }
    // Real API call would go here
    return Promise.reject(new Error('Not implemented'));
  }

  /** Saves validity on the certification record (PUT /certifications/:id). */
  async updateCertificationValidity(
    id: string,
    validity: { validity_type: 'LIFETIME' | 'FIXED_DURATION'; validity_months?: number }
  ): Promise<any> {
    const response = await apiClient.put(API_ENDPOINTS.CERTIFICATIONS.UPDATE(id), validity);
    return response.data.data;
  }

  /** Saves the certificate template chosen per tier (PUT /certifications/:id). Empty tier = default template. */
  async updateCertificationTemplates(
    id: string,
    certificate_template_ids: { GOLD?: string; SILVER?: string; BRONZE?: string }
  ): Promise<any> {
    const response = await apiClient.put(API_ENDPOINTS.CERTIFICATIONS.UPDATE(id), { certificate_template_ids });
    return response.data.data;
  }

  async deleteCertification(id: string): Promise<any> {
    if (this.useMockData) {
      return new Promise((resolve, reject) => {
        setTimeout(() => {
          const index = mockCertificationData.certifications.findIndex(c => c.id === id);
          if (index !== -1) {
            mockCertificationData.certifications.splice(index, 1);
            resolve({ success: true, message: 'Certification deleted successfully' });
          } else {
            reject(new Error('Certification not found'));
          }
        }, 500);
      });
    }
    try {
      const response = await apiClient.delete<any>(`/certifications/${id}`);
      if (response.data.success) {
        if (response?.data?.meta_data) {
          return response?.data;
        } else {
          return { success: true, message: 'Certification deleted successfully' };
        }
      }
      throw new Error(response.data.description || 'Failed to delete certification');
    } catch (error) {
      console.error('Error deleting certification:', error);
      throw error;
    }
  }

  async duplicateCertification(id: string): Promise<any> {
    if (this.useMockData) {
      return new Promise((resolve, reject) => {
        setTimeout(() => {
          const certification = mockCertificationData.certifications.find(c => c.id === id);
          if (certification) {
            const duplicatedCertification = {
              ...certification,
              id: Date.now().toString(),
              name: `${certification.name} (Copy)`,
              status: 'draft',
              createdAt: new Date().toISOString(),
              updatedAt: new Date().toISOString(),
              totalIssued: 0,
              totalCompleted: 0,
            };
            mockCertificationData.certifications.push(duplicatedCertification);
            resolve(duplicatedCertification);
          } else {
            reject(new Error('Certification not found'));
          }
        }, 1000);
      });
    }
    // Real API call would go here
    return Promise.reject(new Error('Not implemented'));
  }

  // Certificate Templates
  async getTemplates(): Promise<any> {
    if (this.useMockData) {
      return new Promise(resolve => {
        setTimeout(() => {
          resolve(mockCertificationData.templates);
        }, 500);
      });
    }
    // Real API call would go here
    return Promise.reject(new Error('Not implemented'));
  }

  async createTemplate(templateData: TemplateData): Promise<any> {
    if (this.useMockData) {
      return new Promise(resolve => {
        setTimeout(() => {
          const newTemplate = {
            id: Date.now().toString(),
            ...templateData,
            createdAt: new Date().toISOString(),
            updatedAt: new Date().toISOString(),
            usageCount: 0,
          };
          mockCertificationData.templates.push(newTemplate);
          resolve(newTemplate);
        }, 1000);
      });
    }
    // Real API call would go here
    return Promise.reject(new Error('Not implemented'));
  }

  async updateTemplate(id: string, templateData: TemplateData): Promise<any> {
    if (this.useMockData) {
      return new Promise((resolve, reject) => {
        setTimeout(() => {
          const index = mockCertificationData.templates.findIndex(t => t.id === id);
          if (index !== -1) {
            mockCertificationData.templates[index] = {
              ...mockCertificationData.templates[index],
              ...templateData,
              updatedAt: new Date().toISOString(),
            };
            resolve(mockCertificationData.templates[index]);
          } else {
            reject(new Error('Template not found'));
          }
        }, 1000);
      });
    }
    // Real API call would go here
    return Promise.reject(new Error('Not implemented'));
  }

  async deleteTemplate(id: string): Promise<{ message: string }> {
    if (this.useMockData) {
      return new Promise((resolve, reject) => {
        setTimeout(() => {
          const index = mockCertificationData.templates.findIndex(t => t.id === id);
          if (index !== -1) {
            mockCertificationData.templates.splice(index, 1);
            resolve({ message: 'Template deleted successfully' });
          } else {
            reject(new Error('Template not found'));
          }
        }, 500);
      });
    }
    // Real API call would go here
    return Promise.reject(new Error('Not implemented'));
  }

  // Issued Certificates
  async getIssuedCertificates(filters: Filters = {}): Promise<any> {
    if (this.useMockData) {
      return new Promise(resolve => {
        setTimeout(() => {
          let certificates = [...mockCertificationData.issuedCertificates];

          if (filters.certificationId) {
            certificates = certificates.filter(c => c.certificationId === filters.certificationId);
          }
          if (filters.userId) {
            certificates = certificates.filter(c => c.userId === filters.userId);
          }
          if (filters.status) {
            certificates = certificates.filter(c => c.status === filters.status);
          }
          if (filters.search) {
            const searchLower = filters.search.toLowerCase();
            certificates = certificates.filter(
              c =>
                c.recipientName.toLowerCase().includes(searchLower) ||
                c.recipientEmail.toLowerCase().includes(searchLower)
            );
          }

          resolve({
            data: certificates,
            total: certificates.length,
            page: filters.page || 1,
            pageSize: filters.pageSize || 10,
          });
        }, 500);
      });
    }
    // Real API call would go here
    return Promise.resolve({ data: [], total: 0, page: 1, pageSize: 10 });
  }

  async issueCertificate(certificationId: string, userId: string): Promise<any> {
    if (this.useMockData) {
      return new Promise((resolve, reject) => {
        setTimeout(() => {
          const certification = mockCertificationData.certifications.find(c => c.id === certificationId);
          if (!certification) {
            reject(new Error('Certification not found'));
            return;
          }

          const newCertificate = {
            id: `cert_${Date.now()}`,
            certificationId,
            certificationName: certification.name,
            userId,
            recipientName: 'John Student', // This would come from user data
            recipientEmail: 'john@example.com',
            certificateNumber: `CERT-${Date.now()}`,
            issuedDate: new Date().toISOString(),
            expiryDate: certification.validityPeriod
              ? new Date(
                  Date.now() + certification.validityPeriod * 24 * 60 * 60 * 1000
                ).toISOString()
              : null,
            status: 'active',
            downloadUrl: `https://certificates.skillzen.com/cert_${Date.now()}.pdf`,
            verificationUrl: `https://verify.skillzen.com/cert_${Date.now()}`,
          };

          mockCertificationData.issuedCertificates.push(newCertificate);

          // Update certification stats
          certification.totalIssued += 1;

          resolve(newCertificate);
        }, 1000);
      });
    }
    // Real API call would go here
    return Promise.reject(new Error('Not implemented'));
  }

  async revokeCertificate(certificateId: string): Promise<{ message: string }> {
    if (this.useMockData) {
      return new Promise((resolve, reject) => {
        setTimeout(() => {
          const index = mockCertificationData.issuedCertificates.findIndex(c => c.id === certificateId);
          if (index !== -1) {
            mockCertificationData.issuedCertificates[index].status = 'revoked';
            mockCertificationData.issuedCertificates[index].revokedDate = new Date().toISOString();
            resolve({ message: 'Certificate revoked successfully' });
          } else {
            reject(new Error('Certificate not found'));
          }
        }, 500);
      });
    }
    // Real API call would go here
    return Promise.reject(new Error('Not implemented'));
  }

  async bulkIssueCertificates(certificationId: string, userIds: string[]): Promise<any> {
    if (this.useMockData) {
      return new Promise(resolve => {
        setTimeout(() => {
          const certificates = userIds.map(userId => {
            const certification = mockCertificationData.certifications.find(c => c.id === certificationId);
            return {
              id: `cert_${Date.now()}_${userId}`,
              certificationId,
              certificationName: certification?.name || 'Unknown Certification',
              userId,
              recipientName: `User ${userId}`,
              recipientEmail: `user${userId}@example.com`,
              certificateNumber: `CERT-${Date.now()}-${userId}`,
              issuedDate: new Date().toISOString(),
              expiryDate: certification?.validityPeriod
                ? new Date(
                    Date.now() + certification.validityPeriod * 24 * 60 * 60 * 1000
                  ).toISOString()
                : null,
              status: 'active',
              downloadUrl: `https://certificates.skillzen.com/cert_${Date.now()}_${userId}.pdf`,
              verificationUrl: `https://verify.skillzen.com/cert_${Date.now()}_${userId}`,
            };
          });

          mockCertificationData.issuedCertificates.push(...certificates);

          // Update certification stats
          const certification = mockCertificationData.certifications.find(c => c.id === certificationId);
          if (certification) {
            certification.totalIssued += certificates.length;
          }

          resolve({
            message: `${certificates.length} certificates issued successfully`,
            certificates,
          });
        }, 2000);
      });
    }
    // Real API call would go here
    return Promise.reject(new Error('Not implemented'));
  }

  // Certificate Progress Tracking
  async getCertificationProgress(certificationId: string): Promise<any> {
    if (this.useMockData) {
      return new Promise(resolve => {
        setTimeout(() => {
          const progress = mockCertificationData.progress.filter(p => p.certificationId === certificationId);
          resolve(progress);
        }, 500);
      });
    }
    // Real API call would go here
    return Promise.reject(new Error('Not implemented'));
  }

  async updateProgress(certificationId: string, userId: string, progressData: ProgressData): Promise<any> {
    if (this.useMockData) {
      return new Promise(resolve => {
        setTimeout(() => {
          const existingProgress = mockCertificationData.progress.find(
            p => p.certificationId === certificationId && p.userId === userId
          );

          if (existingProgress) {
            Object.assign(existingProgress, progressData, { updatedAt: new Date().toISOString() });
            resolve(existingProgress);
          } else {
            const newProgress = {
              id: `progress_${Date.now()}`,
              certificationId,
              userId,
              ...progressData,
              createdAt: new Date().toISOString(),
              updatedAt: new Date().toISOString(),
            };
            mockCertificationData.progress.push(newProgress);
            resolve(newProgress);
          }
        }, 500);
      });
    }
    // Real API call would go here
    return Promise.reject(new Error('Not implemented'));
  }

  // Analytics and Statistics
  async getCertificationAnalytics(certificationId: string): Promise<any> {
    if (this.useMockData) {
      return new Promise(resolve => {
        setTimeout(() => {
          const analytics = mockCertificationData.analytics.find(a => a.certificationId === certificationId) || {
            certificationId,
            totalEnrolled: 0,
            totalCompleted: 0,
            completionRate: 0,
            averageCompletionTime: 0,
            certificatesIssued: 0,
            certificatesActive: 0,
            certificatesRevoked: 0,
            monthlyStats: [],
          };
          resolve(analytics);
        }, 500);
      });
    }
    // Real API call would go here
    return Promise.reject(new Error('Not implemented'));
  }

  async getOverallStats(): Promise<any> {
    if (this.useMockData) {
      return new Promise(resolve => {
        setTimeout(() => {
          const stats = {
            totalCertifications: mockCertificationData.certifications.length,
            activeCertifications: mockCertificationData.certifications.filter(c => c.status === 'active').length,
            totalCertificatesIssued: mockCertificationData.issuedCertificates.length,
            activeCertificates: mockCertificationData.issuedCertificates.filter(c => c.status === 'active').length,
            revokedCertificates: mockCertificationData.issuedCertificates.filter(c => c.status === 'revoked').length,
            averageCompletionTime: 45, // days
            topPerformingCertifications: mockCertificationData.certifications
              .sort((a, b) => b.totalIssued - a.totalIssued)
              .slice(0, 5),
          };
          resolve(stats);
        }, 500);
      });
    }
    // Real API call would go here
    return Promise.reject(new Error('Not implemented'));
  }

  // Certification Details Methods
  async getCertificationDetails(certification_id: string): Promise<Certification> {
    try {
      const response = await apiClient.get(API_ENDPOINTS.CERTIFICATIONS.GET_DETAILS(certification_id));
      return response.data.data;
    } catch (error) {
      console.error('Error fetching certification details:', error);
      throw error;
    }
  }

  async addQuestion(exam_id: string, question: AddQuestionDto): Promise<CertificationExam> {
    try {
      const response = await apiClient.post(API_ENDPOINTS.CERTIFICATIONS.ADD_QUESTION(exam_id), question);
      return response.data.data;
    } catch (error) {
      console.error('Error adding question:', error);
      throw error;
    }
  }

  async updateQuestions(exam_id: string, questions: CertificationExamQuestion[]): Promise<CertificationExam> {
    try {
      const response = await apiClient.put(API_ENDPOINTS.CERTIFICATIONS.UPDATE_QUESTIONS(exam_id), { questions });
      return response.data.data;
    } catch (error) {
      console.error('Error updating questions:', error);
      throw error;
    }
  }

  async removeQuestion(exam_id: string, question_id: string): Promise<CertificationExam> {
    try {
      const response = await apiClient.delete(API_ENDPOINTS.CERTIFICATIONS.REMOVE_QUESTION(exam_id, question_id));
      return response.data.data;
    } catch (error) {
      console.error('Error removing question:', error);
      throw error;
    }
  }

  async publishExam(exam_id: string, status: 'PUBLISH' | 'DRAFT'): Promise<CertificationExam> {
    try {
      const response = await apiClient.post(API_ENDPOINTS.CERTIFICATIONS.PUBLISH(exam_id), null, {
        params: { status }
      });
      return response.data.data;
    } catch (error) {
      console.error('Error publishing exam:', error);
      throw error;
    }
  }

  async updateExamSettings(exam_id: string, settings: UpdateExamSettingsDto): Promise<CertificationExam> {
    try {
      const response = await apiClient.put(API_ENDPOINTS.CERTIFICATIONS.UPDATE_SETTINGS(exam_id), settings);
      return response.data.data;
    } catch (error) {
      console.error('Error updating exam settings:', error);
      throw error;
    }
  }

  async getRecentCertifications(filters: CertificationFilterDto = {}): Promise<RecentCertificationListResponseDto> {
    try {
      const response = await apiClient.post(API_ENDPOINTS.CERTIFICATIONS.RECENT_GENERATIONS, filters);
      // API returns data in response.data.data format
      const apiData = response.data.data || response.data;
      console.log('✅ Recent Certifications API Response:', apiData);

      // Ensure proper structure matching API response
      const total = apiData.total || 0;
      const limit = filters.limit || apiData.pagination?.limit || 6;
      const skip = filters.skip || apiData.pagination?.skip || 0;
      const totalPages = apiData.pagination?.totalPages || Math.ceil(total / limit);

      return {
        certifications: apiData.certifications || [],
        total: total,
        pagination: {
          total: total,
          limit: limit,
          skip: skip,
          totalPages: totalPages
        }
      };
    } catch (error) {
      console.error('❌ Error fetching recent certifications:', error);
      throw error;
    }
  }

  async getCertificationGenerationStatus(id: string): Promise<any> {
    try {
      const response = await apiClient.get(API_ENDPOINTS.CERTIFICATIONS.GET_STATUS(id));
      return response.data.data;
    } catch (error) {
      console.error('Error fetching certification generation status:', error);
      throw error;
    }
  }

  async retryExamGeneration(id: string): Promise<any> {
    try {
      const response = await apiClient.post(API_ENDPOINTS.CERTIFICATIONS.RETRY_GENERATION(id));
      return response.data;
    } catch (error) {
      console.error('Error retrying exam generation:', error);
      throw error;
    }
  }

  /**
   * Enhance user input for certification creation using AI
   * Returns enhanced input with suggestions and key concepts
   */
  async enhanceUserInput(params: {
    user_input: string;
    targeted_audience?: string;
    // DESCRIPTION: learner-facing description (manual creation); omitted: AI generation brief
    purpose?: 'GENERATION_INPUT' | 'DESCRIPTION';
  }): Promise<{
    enhanced_input: string;
    suggestions: string[];
    key_concepts: string[];
  }> {
    try {
      const response = await apiClient.post(API_ENDPOINTS.CERTIFICATIONS.ENHANCE_INPUT, params);
      return response.data.data;
    } catch (error) {
      console.error('Error enhancing user input:', error);
      throw error;
    }
  }

  /**
   * Pre-flight: does the selected content match the description?
   *
   * Read-only, creates nothing. Safe to call whenever the selection changes.
   * Never rejects on its own failure — the backend returns outcome 'ok' with
   * check_skipped when it cannot run, so a failed check never blocks creation.
   */
  async validateContentSelection(params: {
    user_input: string;
    required_course_ids?: string[];
    learning_path_ids?: string[];
    knowledge_store_id?: string;
  }): Promise<ContentSelectionValidationResult> {
    const response = await apiClient.post(
      API_ENDPOINTS.CERTIFICATIONS.VALIDATE_SELECTION,
      params
    );
    return response.data.data;
  }

  // Get current user's certifications with progress
  async getUserCertifications(): Promise<UserCertificationSummaryDto[]> {
    try {
      const response = await apiClient.get(API_ENDPOINTS.CERTIFICATIONS.MY_CERTIFICATIONS);
      return response.data.data || [];
    } catch (error) {
      console.error('Error fetching user certifications:', error);
      throw error;
    }
  }
}

export const certificationService = new CertificationService();